import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import NavigationLayout from '../../components/NavigationLayout';
import CustomTable from '../../components/table/CustomTable';
import { PRIMARY_OWNER } from '../../constants/accountTypes';
import './roleManagement.css';

const TABLE_HEADERS = [
  {
    label: 'Role Name',
    sortable: true,
    sortKey: 'name',
    colSpan: 1.5,
  },
  {
    label: 'Level',
    sortable: true,
    sortKey: 'level',
    colSpan: 0.5,
  },
  {
    label: 'Description',
    sortable: false,
    colSpan: 2,
  },
  {
    label: 'Permissions',
    sortable: false,
    colSpan: 1.5,
  },
  {
    label: 'Type',
    sortable: false,
    colSpan: 0.8,
  },
  {
    label: 'Actions',
    sortable: false,
    colSpan: 0.7,
  },
];

function RoleManagement() {
  const {
    activeWorkspace: workspace, accessToken, member: currentMember, refreshWorkspaceToken,
  } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [openAssignRoleDialog, setOpenAssignRoleDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [members, setMembers] = useState([]);

  // Form state
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [roleLevel, setRoleLevel] = useState(1);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const fetchRoles = useCallback(async () => {
    if (!workspace?.id || !accessToken) return;

    try {
      setLoading(true);
      const response = await sendRequest({
        url: API.ROLES.getRoles(workspace.id),
        accessToken,
      });

      if (response.data?.roles) {
        setRoles(response.data.roles);
      }
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to fetch roles',
      });
    } finally {
      setLoading(false);
    }
  }, [workspace?.id, accessToken, displayNotification]);

  const fetchPermissions = useCallback(async () => {
    if (!workspace?.id || !accessToken) return;

    try {
      const response = await sendRequest({
        url: API.ROLES.getPermissions(workspace.id),
        accessToken,
      });

      if (response.data?.permissions) {
        setPermissions(response.data.permissions);
      }
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to fetch permissions',
      });
    }
  }, [workspace?.id, accessToken, displayNotification]);

  const fetchMembers = useCallback(async () => {
    if (!workspace?.id || !accessToken) return;

    try {
      const response = await sendRequest({
        url: API.WORKSPACE.members(workspace.id),
        accessToken,
      });

      if (response.data?.members) {
        setMembers(response.data.members);
      }
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to fetch members',
      });
    }
  }, [workspace?.id, accessToken, displayNotification]);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchMembers();
  }, [fetchRoles, fetchPermissions, fetchMembers]);

  const handleOpenDialog = (role = null) => {
    if (role) {
      setSelectedRole(role);
      setRoleName(role.name);
      setRoleDescription(role.description || '');
      setRoleLevel(role.level);
      setSelectedPermissions(role.permissions?.map((p) => p.id) || []);
    } else {
      setSelectedRole(null);
      setRoleName('');
      setRoleDescription('');
      setRoleLevel(1);
      setSelectedPermissions([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRole(null);
    setRoleName('');
    setRoleDescription('');
    setRoleLevel(1);
    setSelectedPermissions([]);
  };

  const handleSaveRole = async () => {
    if (!workspace?.id || !accessToken || !roleName) return;

    try {
      const payload = {
        name: roleName,
        description: roleDescription,
        level: roleLevel,
        permissionIds: selectedPermissions,
      };

      let needsRefresh = false;

      if (selectedRole) {
        // Update existing role
        await sendRequest({
          url: API.ROLES.updateRole(workspace.id, selectedRole.id),
          method: 'PUT',
          accessToken,
          data: payload,
        });

        // Check if current user has this role
        if (currentMember && currentMember.roleId === selectedRole.id) {
          needsRefresh = true;
        }

        displayNotification({
          severity: 'success',
          message: 'Role updated successfully',
        });
      } else {
        // Create new role
        await sendRequest({
          url: API.ROLES.createRole(workspace.id),
          method: 'POST',
          accessToken,
          data: payload,
        });
        displayNotification({
          severity: 'success',
          message: 'Role created successfully',
        });
      }

      handleCloseDialog();
      fetchRoles();

      // Refresh token if current user's role was modified
      if (needsRefresh) {
        await refreshWorkspaceToken();
      }
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to save role',
      });
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!workspace?.id || !accessToken) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      await sendRequest({
        url: API.ROLES.deleteRole(workspace.id, roleId),
        method: 'DELETE',
        accessToken,
      });
      displayNotification({
        severity: 'success',
        message: 'Role deleted successfully',
      });
      fetchRoles();
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to delete role',
      });
    }
  };

  const handleOpenPermissionDialog = (role) => {
    setSelectedRole(role);
    setSelectedPermissions(role.permissions?.map((p) => p.id) || []);
    setOpenPermissionDialog(true);
  };

  const handleSavePermissions = async () => {
    if (!workspace?.id || !accessToken || !selectedRole) return;

    try {
      await sendRequest({
        url: API.ROLES.assignPermissions(workspace.id, selectedRole.id),
        method: 'POST',
        accessToken,
        data: { permissionIds: selectedPermissions },
      });
      displayNotification({
        severity: 'success',
        message: 'Permissions updated successfully',
      });

      // If the current user has this role, refresh their token
      if (currentMember && currentMember.roleId === selectedRole.id) {
        await refreshWorkspaceToken();
      }

      setOpenPermissionDialog(false);
      fetchRoles();
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to update permissions',
      });
    }
  };

  const handleOpenAssignRoleDialog = () => {
    setOpenAssignRoleDialog(true);
  };

  const handleAssignRole = async (memberId, roleId) => {
    if (!workspace?.id || !accessToken) return;

    try {
      await sendRequest({
        url: API.WORKSPACE.editMember(workspace.id, memberId),
        method: 'PUT',
        accessToken,
        data: { updateData: { roleId } },
      });
      displayNotification({
        severity: 'success',
        message: 'Role assigned successfully',
      });

      // If the current user's role was changed, refresh their token
      if (currentMember && currentMember.id === Number(memberId)) {
        await refreshWorkspaceToken();
      }

      fetchMembers();
    } catch (error) {
      displayNotification({
        severity: 'error',
        message: error.message || 'Failed to assign role',
      });
    }
  };

  const groupPermissionsByResource = (perms) => {
    const grouped = {};
    perms.forEach((perm) => {
      if (!grouped[perm.resource]) {
        grouped[perm.resource] = [];
      }
      grouped[perm.resource].push(perm);
    });
    return grouped;
  };

  // Optimized renderTableRows function
  const renderTableRows = useCallback(() => {
    if (!Array.isArray(roles) || roles.length === 0) return [];
    return roles.map((role) => [
      <div key={`name-${role.id}`} className="role-name">{role.name}</div>,
      <div key={`level-${role.id}`} className="role-level">
        <Chip label={role.level} size="small" color="primary" />
      </div>,
      <div key={`desc-${role.id}`} className="role-description">
        {role.description || '-'}
      </div>,
      <div key={`perms-${role.id}`} className="role-permissions">
        {role.permissions?.length || 0}
        {' '}
        permissions
      </div>,
      <div key={`type-${role.id}`}>
        {role.isSystemRole ? (
          <Chip label="System" size="small" color="default" />
        ) : (
          <Chip label="Custom" size="small" color="secondary" />
        )}
      </div>,
      <div key={`actions-${role.id}`} className="role-actions">
        {!role.isSystemRole && (
          <>
            <Tooltip title="Edit Role">
              <IconButton size="small" onClick={() => handleOpenDialog(role)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Role">
              <IconButton size="small" onClick={() => handleDeleteRole(role.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title="Manage Permissions">
          <IconButton size="small" onClick={() => handleOpenPermissionDialog(role)}>
            <EditIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      </div>,
    ]);
  }, [roles]);

  const groupedPermissions = groupPermissionsByResource(permissions);

  return (
    <NavigationLayout>
      <div className="role-management-container">
        <div className="role-management-header">
          <h1>Role Management</h1>
          <div className="header-actions">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchRoles}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Role
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleOpenAssignRoleDialog}
            >
              Assign Roles to Users
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <CircularProgress />
          </div>
        ) : (
          <CustomTable headers={TABLE_HEADERS} items={renderTableRows()} />
        )}

        {/* Create/Edit Role Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedRole ? 'Edit Role' : 'Create New Role'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 2, mt: 2,
            }}
            >
              <TextField
                label="Role Name"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="Level"
                type="number"
                value={roleLevel}
                onChange={(e) => setRoleLevel(parseInt(e.target.value, 10))}
                fullWidth
                required
                inputProps={{ min: 1, max: 5 }}
                helperText="Level 1-5 (higher = more permissions)"
              />
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Select Permissions:
              </Typography>
              <Box sx={{
                maxHeight: 300, overflow: 'auto', border: '1px solid #ddd', p: 2, borderRadius: 1,
              }}
              >
                {Object.entries(groupedPermissions).map(([resource, perms]) => (
                  <Box key={resource} sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'bold', textTransform: 'capitalize', mb: 1 }}
                    >
                      {resource}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {perms.map((perm) => (
                        <Chip
                          key={perm.id}
                          label={perm.action}
                          onClick={() => {
                            setSelectedPermissions((prev) => (prev.includes(perm.id)
                              ? prev.filter((id) => id !== perm.id)
                              : [...prev, perm.id]));
                          }}
                          color={selectedPermissions.includes(perm.id) ? 'primary' : 'default'}
                          variant={selectedPermissions.includes(perm.id) ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSaveRole} variant="contained" disabled={!roleName}>
              {selectedRole ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manage Permissions Dialog */}
        <Dialog
          open={openPermissionDialog}
          onClose={() => setOpenPermissionDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Manage Permissions for
            {' '}
            {selectedRole?.name}
            {selectedRole?.isSystemRole && (
              <Chip label="System Role" size="small" color="warning" sx={{ ml: 2 }} />
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ maxHeight: 400, overflow: 'auto', mt: 2 }}>
              {Object.entries(groupedPermissions).map(([resource, perms]) => (
                <Box key={resource} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textTransform: 'capitalize', mb: 1 }}>
                    {resource}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {perms.map((perm) => (
                      <Chip
                        key={perm.id}
                        label={`${perm.action}${perm.description ? ` - ${perm.description}` : ''}`}
                        onClick={() => {
                          if (selectedRole?.isSystemRole) return;
                          setSelectedPermissions((prev) => (prev.includes(perm.id)
                            ? prev.filter((id) => id !== perm.id)
                            : [...prev, perm.id]));
                        }}
                        color={selectedPermissions.includes(perm.id) ? 'primary' : 'default'}
                        variant={selectedPermissions.includes(perm.id) ? 'filled' : 'outlined'}
                        disabled={selectedRole?.isSystemRole}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
            {selectedRole?.isSystemRole && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 2, display: 'block' }}>
                System roles cannot be modified. You can view permissions only.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPermissionDialog(false)}>
              {selectedRole?.isSystemRole ? 'Close' : 'Cancel'}
            </Button>
            {!selectedRole?.isSystemRole && (
              <Button onClick={handleSavePermissions} variant="contained">
                Save Permissions
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Assign Roles to Users Dialog */}
        <Dialog
          open={openAssignRoleDialog}
          onClose={() => setOpenAssignRoleDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Assign Roles to Users</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {members.filter((member) => member.status && member.accountType !== PRIMARY_OWNER).map((member) => (
                <Box
                  key={member.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <Typography>
                    {member.user?.firstName}
                    {' '}
                    {member.user?.lastName}
                    {member.role && (
                      <Chip
                        label={member.role.name}
                        size="small"
                        sx={{ ml: 2 }}
                        color="primary"
                      />
                    )}
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={member.roleId || ''}
                    onChange={(e) => handleAssignRole(member.id, e.target.value)}
                    sx={{ minWidth: 200 }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                        {' '}
                        (Level
                        {role.level}
                        )
                      </option>
                    ))}
                  </TextField>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAssignRoleDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </NavigationLayout>
  );
}

export default RoleManagement;
